/**
 * iOS Safari / mobile Chrome: blur + viewport meta tick + scroll reset when returning
 * from another app (fixes stuck pinch/zoom and keyboard zoom).
 */

const VIEWPORT_BASE =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, interactive-widget=resizes-content';

/** iOS Safari(크롬·PWA 제외): fixed bottom은 layout viewport 기준 → innerHeight 사용 */
function isIOSSafariBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (!/iPhone|iPad|iPod/i.test(ua)) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua);
}

function shouldPreserveActiveFocus() {
  const el = document.activeElement;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.closest('.mingo-chatv2-input')) return true;
  if (el.getAttribute?.('data-keep-focus') === 'true') return true;
  return false;
}

export function syncMobileViewportHeight() {
  if (typeof window === 'undefined') return;
  // Safari: visualViewport는 툴바 제외 영역이라 shell을 줄이면 fixed 탭·하단에 빈 공간 생김
  const h = isIOSSafariBrowser()
    ? window.innerHeight
    : (window.visualViewport?.height ?? window.innerHeight);
  document.documentElement.style.setProperty('--mingo-vh', `${h * 0.01}px`);
}

export function resetMobileViewportZoom(options = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const active = document.activeElement;
  const keepFocus = options.keepFocus || shouldPreserveActiveFocus();
  if (!keepFocus && active && typeof active.blur === 'function') {
    active.blur();
  }

  const meta = document.querySelector('meta[name="viewport"]');
  if (meta) {
    meta.setAttribute('content', `${VIEWPORT_BASE}, maximum-scale=1.0`);
    void document.body?.offsetHeight;
    meta.setAttribute('content', VIEWPORT_BASE);
  }

  window.scrollTo(0, 0);
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  syncMobileViewportHeight();
}

function onVisible() {
  if (document.visibilityState !== 'visible') return;
  window.requestAnimationFrame(() => {
    // Safari: meta 흔들기·scrollTo(0)가 하단탭 자리에 빈 공간 유발 → 높이만 동기화
    if (isIOSSafariBrowser()) {
      syncMobileViewportHeight();
      requestAnimationFrame(() => syncMobileViewportHeight());
      return;
    }
    resetMobileViewportZoom();
  });
}

/**
 * @returns {() => void} cleanup
 */
export function installMobileViewportFix() {
  if (typeof window === 'undefined') return () => {};

  syncMobileViewportHeight();
  requestAnimationFrame(() => syncMobileViewportHeight());

  let raf = 0;
  const onViewportChange = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      syncMobileViewportHeight();
    });
  };
  // 새로고침·첫 진입: viewport meta 흔들면 Safari 하단탭·빈 공간 깨짐 → 높이만 동기화
  const onPageShow = (event) => {
    if (event?.persisted) {
      resetMobileViewportZoom();
      return;
    }
    syncMobileViewportHeight();
    requestAnimationFrame(() => syncMobileViewportHeight());
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('orientationchange', onViewportChange);
  window.visualViewport?.addEventListener('resize', onViewportChange);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('resize', onViewportChange);
    window.removeEventListener('orientationchange', onViewportChange);
    window.visualViewport?.removeEventListener('resize', onViewportChange);
    if (raf) cancelAnimationFrame(raf);
  };
}
